import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { useApolloClient } from '@apollo/client/react';
import {
  Department,
  DepartmentsSearchListDocument,
  DepartmentsSearchListQuery,
  DepartmentsSearchListQueryVariables,
  FilterGroupingEnum,
  SortDirectionEnum,
} from 'graphql-common';
import PaginatedList, {
  PaginatedListMetadata,
} from '@lib/components/PaginatedList/PaginatedList';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import InterventionDepartmentsModal from 'components/InterventionDepartmentsModal/InterventionDepartmentsModal';

type Props = {
  interventionId: string;
  onShowAllClick: () => void;
  isModalOpen: boolean;
  onModalClose: () => void;
};

export default function InterventionDepartmentsList({
  interventionId,
  onShowAllClick,
  isModalOpen,
  onModalClose,
}: Props) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const fetchDepartments = useCallback(
    (options: { variables: DepartmentsSearchListQueryVariables }) =>
      client.query<
        DepartmentsSearchListQuery,
        DepartmentsSearchListQueryVariables
      >({
        query: DepartmentsSearchListDocument,
        variables: options.variables,
        fetchPolicy: 'no-cache',
      }),
    [client],
  );
  const [hasLinkedDepartments, setHasLinkedDepartments] = useState(false);

  return (
    <>
      <PaginatedList<
        Department,
        DepartmentsSearchListQuery,
        DepartmentsSearchListQueryVariables
      >
        query={fetchDepartments}
        variables={{
          sorts: { name: SortDirectionEnum.Asc },
          filters: [
            {
              departmentInterventionsInterventionId: {
                grouping: FilterGroupingEnum.And,
                predicate: { eq: interventionId },
              },
            },
          ],
        }}
        useShowMore={false}
        onShowAllClick={onShowAllClick}
        onMetadataChange={(metadata) =>
          setHasLinkedDepartments((metadata?.totalCount || 0) > 0)
        }
        getCollection={(data) =>
          _get(data, 'data.collection', []) as Department[]
        }
        getMetadata={(data) =>
          _get(data, 'data.metadata') as PaginatedListMetadata | undefined
        }
        getItemKey={(item) => item.id}
        renderItem={(item) => (
          <Chip
            type={colorToChipType(item.color)}
            label={item.name}
            leftIcon="circle"
          />
        )}
        renderEmptyPreview={(onClick) => (
          <Chip type={ChipTypes.chipGray} label={t('all')} onClick={onClick} />
        )}
      />
      <InterventionDepartmentsModal
        hasLinkedDepartments={hasLinkedDepartments}
        interventionId={interventionId}
        isOpen={isModalOpen}
        onClose={onModalClose}
      />
    </>
  );
}
